import { api } from './api';

// ============================================================
// Types
// ============================================================

export type HubCampaignKind = 'collection' | 'database';

export interface HubCampaignCard {
  id: number;
  title: string;
  kind: HubCampaignKind;
  entity_type: string | null;
  description: string | null;
  image_url: string | null;
  status: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  attribute_count: number;
}

export interface HubAttribute {
  id: number;
  name: string;
  description: string | null;
  attribute_type: 'numeric' | 'boolean' | 'enum' | 'scale';
  config: Record<string, any>;
  note_mode: 'disabled' | 'optional' | 'required';
  points_reward: number;
  min_submissions: number;
  submission_count: number;
  data_mode: 'aggregated' | 'factual';
  display_order: number;
}

export interface HubCampaignDetail {
  id: number;
  title: string;
  kind: HubCampaignKind;
  entity_type: string | null;
  description: string | null;
  image_url: string | null;
  status: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  attributes: HubAttribute[];
  created_at: string;
}

// ---- database種別 (Wiki型) : Character 第1号 ----

export interface HubCharacterCard {
  character_id: number;
  name: string;
  japanese_name: string | null;
  image_url: string | null;
  game_count: number;
}

export interface HubCharacterGame {
  id: number;
  name: string;
  cover_image: string | null;
}

export interface HubCharacterDetail {
  character_id: number;
  name: string;
  japanese_name: string | null;
  description: string | null;
  image_url: string | null;
  status: number;
  game_ids: number[];
  games: HubCharacterGame[];
  has_pending: boolean;
}

export interface HubContribution {
  id: number;
  entity_type: string;
  target_id: number | null;
  action: number;
  status: number;
  reason: string | null;
  points_awarded: number;
  created_at: string;
}

export interface HubCharacterPayload {
  name: string;
  japanese_name?: string | null;
  description?: string | null;
  image_url?: string | null;
  game_ids: number[];
}

export interface HubSubmission {
  id: number;
  attribute_id: number;
  game_id: number;
  username: string;
  value: { v: any };
  note: string | null;
  reason: string | null;
  is_edit: boolean;
  status: number;
  points_awarded: number;
  created_at: string;
}

export interface HubAggregatedValue {
  id: number;
  attribute_id: number;
  game_id: number;
  attribute_name: string;
  attribute_type: string;
  attribute_config: Record<string, any>;
  submission_count: number;
  aggregated_value: Record<string, any>;
  is_published: boolean;
  last_calculated_at: string;
}

export interface HubPointsHistory {
  id: number;
  amount: number;
  source_type: string;
  source_id: number | null;
  source_detail: {
    kind?: HubCampaignKind;
    attribute_name?: string;
    entity_type?: string;
    entity_label?: string;
    is_edit: boolean;
  } | null;
  description: string | null;
  balance_after: number;
  created_at: string;
}

export interface HubLeaderboardEntry {
  rank: number;
  username: string;
  display_name: string;
  avatar: string | null;
  contribution_points: number;
  submission_count: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Admin types
export interface AdminHubCampaign {
  id: number;
  title: string;
  kind: HubCampaignKind;
  entity_type: string | null;
  points_create: number;
  points_edit: number;
  description: string | null;
  image_url: string | null;
  status: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface AdminHubContribution {
  id: number;
  entity_type: string;
  target_id: number | null;
  campaign_id: number | null;
  user_id: number;
  username: string;
  action: number;            // 1=作成 2=編集
  payload: HubCharacterPayload & Record<string, any>;
  reason: string | null;
  status: number;
  reviewed_by: number | null;
  reviewed_at: string | null;
  points_awarded: number;
  current_value: (HubCharacterDetail & Record<string, any>) | null;
  games_meta: Record<string, string>;   // game_id -> 名前
  created_at: string;
}

export interface AdminHubAttribute {
  id: number;
  campaign: number;
  name: string;
  description: string | null;
  attribute_type: string;
  config: Record<string, any>;
  aggregation_method: string;
  note_mode: string;
  approval_mode: string;
  data_mode: string;
  min_submissions: number;
  points_reward: number;
  status: number;
  display_order: number;
  target_content_type: string;
  created_at: string;
  updated_at: string;
}

export interface AdminHubSubmission {
  id: number;
  attribute_id: number;
  attribute_name: string;
  attribute_type: string;
  attribute_config: Record<string, any>;
  data_mode: string;
  user_id: number;
  username: string;
  game_id: number;
  game_name: string | null;
  value: { v: any };
  note: string | null;
  reason: string | null;
  is_edit: boolean;
  status: number;
  reviewed_by: number | null;
  reviewed_at: string | null;
  points_awarded: number;
  created_at: string;
  previous_value: any | null;
  previous_note: string | null;
}

export interface AdminHubStats {
  campaigns_active: number;
  attributes_active: number;
  submissions_total: number;
  submissions_pending: number;
  submissions_approved: number;
}

export interface HubOptions {
  announcement: {
    enabled: boolean;
    text: string;
    severity: 'info' | 'warning' | 'error' | 'success';
  };
  lock: {
    locked: boolean;
    message: string;
  };
}

// ============================================================
// Public API
// ============================================================

export const hubGetOptions = async (): Promise<HubOptions> => {
  const response = await api.get('/hub/options/');
  return response.data;
};

export const hubGetCampaigns = async (): Promise<HubCampaignCard[]> => {
  const response = await api.get('/hub/campaigns/');
  return response.data;
};

export const hubGetCampaignDetail = async (id: number): Promise<HubCampaignDetail> => {
  const response = await api.get(`/hub/campaigns/${id}/`);
  return response.data;
};

export const hubGetGameAttributes = async (gameId: number): Promise<HubAggregatedValue[]> => {
  const response = await api.get(`/hub/games/${gameId}/attributes/`);
  return response.data;
};

export const hubSubmitData = async (data: {
  attribute_id: number;
  game_id: number;
  value: { v: any };
  note?: string;
  reason?: string;
}): Promise<HubSubmission> => {
  const response = await api.post('/hub/submissions/', data);
  return response.data;
};

export const hubGetMySubmissions = async (params?: {
  game_id?: number;
  attribute_id?: number;
  page?: number;
}): Promise<PaginatedResponse<HubSubmission>> => {
  const urlParams = new URLSearchParams();
  if (params?.game_id) urlParams.append('game_id', params.game_id.toString());
  if (params?.attribute_id) urlParams.append('attribute_id', params.attribute_id.toString());
  if (params?.page) urlParams.append('page', params.page.toString());
  const response = await api.get(`/hub/submissions/mine/?${urlParams.toString()}`);
  return response.data;
};

export const hubGetMyGameSubmissions = async (gameId: number): Promise<{
  submissions: HubSubmission[];
  pending_attribute_ids: number[];
}> => {
  const response = await api.get(`/hub/games/${gameId}/my-submissions/`);
  return response.data;
};

export const hubGetMyGameConfirmations = async (gameId: number): Promise<number[]> => {
  const response = await api.get(`/hub/games/${gameId}/my-confirmations/`);
  return response.data;
};

export const hubConfirmValue = async (data: {
  attribute_id: number;
  game_id: number;
}): Promise<{ status: string }> => {
  const response = await api.post('/hub/confirmations/', data);
  return response.data;
};

export const hubGetPointsHistory = async (page?: number): Promise<PaginatedResponse<HubPointsHistory>> => {
  const urlParams = new URLSearchParams();
  if (page) urlParams.append('page', page.toString());
  const response = await api.get(`/hub/points/history/?${urlParams.toString()}`);
  return response.data;
};

export const hubGetLeaderboard = async (): Promise<HubLeaderboardEntry[]> => {
  const response = await api.get('/hub/leaderboard/');
  return response.data;
};

// ---- database種別 (Wiki型) : Character ----

export const hubListCharacters = async (params?: {
  q?: string;
  game_id?: number;
  page?: number;
}): Promise<PaginatedResponse<HubCharacterCard>> => {
  const urlParams = new URLSearchParams();
  if (params?.q) urlParams.append('q', params.q);
  if (params?.game_id) urlParams.append('game_id', params.game_id.toString());
  if (params?.page) urlParams.append('page', params.page.toString());
  const response = await api.get(`/hub/characters/?${urlParams.toString()}`);
  return response.data;
};

export const hubGetCharacter = async (characterId: number): Promise<HubCharacterDetail> => {
  const response = await api.get(`/hub/characters/${characterId}/`);
  return response.data;
};

export const hubSubmitContribution = async (data: {
  campaign_id: number;
  target_id?: number | null;
  payload: HubCharacterPayload;
  reason?: string;
}): Promise<HubContribution> => {
  const response = await api.post('/hub/contributions/', data);
  return response.data;
};

export const hubUploadContributionImage = async (file: File): Promise<{ image_url: string }> => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post('/hub/contributions/upload-image/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const hubGetMyContributions = async (params?: {
  entity_type?: string;
  page?: number;
}): Promise<PaginatedResponse<HubContribution>> => {
  const urlParams = new URLSearchParams();
  if (params?.entity_type) urlParams.append('entity_type', params.entity_type);
  if (params?.page) urlParams.append('page', params.page.toString());
  const response = await api.get(`/hub/contributions/mine/?${urlParams.toString()}`);
  return response.data;
};

// ============================================================
// Admin API
// ============================================================

export const adminHubGetCampaigns = async (): Promise<AdminHubCampaign[]> => {
  const response = await api.get('/admin/hub/campaigns/');
  return response.data;
};

export const adminHubCreateCampaign = async (data: Partial<AdminHubCampaign>): Promise<AdminHubCampaign> => {
  const response = await api.post('/admin/hub/campaigns/', data);
  return response.data;
};

export const adminHubUpdateCampaign = async (id: number, data: Partial<AdminHubCampaign>): Promise<AdminHubCampaign> => {
  const response = await api.patch(`/admin/hub/campaigns/${id}/`, data);
  return response.data;
};

export const adminHubDeleteCampaign = async (id: number): Promise<void> => {
  await api.delete(`/admin/hub/campaigns/${id}/`);
};

export const adminHubGetAttributes = async (campaignId?: number): Promise<AdminHubAttribute[]> => {
  const urlParams = new URLSearchParams();
  if (campaignId) urlParams.append('campaign_id', campaignId.toString());
  const response = await api.get(`/admin/hub/attributes/?${urlParams.toString()}`);
  return response.data;
};

export const adminHubCreateAttribute = async (data: Partial<AdminHubAttribute>): Promise<AdminHubAttribute> => {
  const response = await api.post('/admin/hub/attributes/', data);
  return response.data;
};

export const adminHubUpdateAttribute = async (id: number, data: Partial<AdminHubAttribute>): Promise<AdminHubAttribute> => {
  const response = await api.patch(`/admin/hub/attributes/${id}/`, data);
  return response.data;
};

export const adminHubDeleteAttribute = async (id: number): Promise<void> => {
  await api.delete(`/admin/hub/attributes/${id}/`);
};

export const adminHubGetPendingSubmissions = async (params?: {
  campaign_id?: number;
  attribute_id?: number;
}): Promise<AdminHubSubmission[]> => {
  const urlParams = new URLSearchParams();
  if (params?.campaign_id) urlParams.append('campaign_id', params.campaign_id.toString());
  if (params?.attribute_id) urlParams.append('attribute_id', params.attribute_id.toString());
  const response = await api.get(`/admin/hub/submissions/pending/?${urlParams.toString()}`);
  return response.data;
};

export const adminHubApproveSubmission = async (id: number): Promise<AdminHubSubmission> => {
  const response = await api.post(`/admin/hub/submissions/${id}/approve/`);
  return response.data;
};

export const adminHubRejectSubmission = async (id: number): Promise<AdminHubSubmission> => {
  const response = await api.post(`/admin/hub/submissions/${id}/reject/`);
  return response.data;
};

export const adminHubBatchApprove = async (ids: number[]): Promise<{ approved: number[]; errors: any[] }> => {
  const response = await api.post('/admin/hub/submissions/batch-approve/', { ids });
  return response.data;
};

export const adminHubBatchReject = async (ids: number[]): Promise<{ rejected: number[]; errors: any[] }> => {
  const response = await api.post('/admin/hub/submissions/batch-reject/', { ids });
  return response.data;
};

export const adminHubGetStats = async (): Promise<AdminHubStats> => {
  const response = await api.get('/admin/hub/stats/');
  return response.data;
};

// ---- database種別 (Wiki型) 貢献レビュー ----

export const adminHubGetPendingContributions = async (entityType?: string): Promise<AdminHubContribution[]> => {
  const urlParams = new URLSearchParams();
  if (entityType) urlParams.append('entity_type', entityType);
  const response = await api.get(`/admin/hub/contributions/pending/?${urlParams.toString()}`);
  return response.data;
};

export const adminHubApproveContribution = async (id: number): Promise<AdminHubContribution> => {
  const response = await api.post(`/admin/hub/contributions/${id}/approve/`);
  return response.data;
};

export const adminHubRejectContribution = async (id: number): Promise<AdminHubContribution> => {
  const response = await api.post(`/admin/hub/contributions/${id}/reject/`);
  return response.data;
};

export const adminHubGetSettings = async (): Promise<HubOptions> => {
  const response = await api.get('/admin/hub/settings/');
  return response.data;
};

export const adminHubUpdateSettings = async (data: Partial<HubOptions>): Promise<Partial<HubOptions>> => {
  const response = await api.patch('/admin/hub/settings/', data);
  return response.data;
};
